import React, { useState, useEffect } from "react";
import ConfirmModal from "../components/ConfirmModal";
import { classifyProvisionError } from "../utils/provisionError";

const API = process.env.REACT_APP_API_URL || "";

// Fixed spec, same idea as Tencent's manual-instance buttons — one click,
// no per-order cores/RAM/disk/password picking.
const SPEC_CORES = 16;
const SPEC_RAM = 64;
const SPEC_DISK = 60;

const StatusBadge = ({ status, isBuilding, isFailed }) => {
  if (isFailed) return <span className="adm-badge adm-badge-red">✕ Failed</span>;
  if (isBuilding) return <span className="adm-badge adm-badge-amber">◌ Building</span>;
  const map = {
    running: { cls: "adm-badge adm-badge-green", label: "● Running" },
    stopped: { cls: "adm-badge adm-badge-red",   label: "○ Stopped" },
  };
  const key = (status || "").toLowerCase();
  const s = map[key] || { cls: "adm-badge adm-badge-gray", label: status || "Unknown" };
  return <span className={s.cls}>{s.label}</span>;
};

const SpecBadge = ({ cores, ram, disk }) => (
  <span className="adm-badge" style={{ background: "rgba(56,189,248,0.12)", color: "#38bdf8", border: "1px solid rgba(56,189,248,0.35)" }}>
    {cores}c / {ram}GB{disk ? ` / ${disk}GB disk` : ""}
  </span>
);

const STAGE_LABELS = {
  provisioning_vm:     "Waiting for VPS to boot...",
  booting:             "Booting instance...",
  installing_deps:     "Installing dependencies...",
  installing_chromium: "Installing Chromium...",
  starting_service:    "Starting agent service...",
  online:              "Online",
  failed:              "Install failed",
};

const ProvisionBar = ({ stage, pct, error }) => {
  if (!stage || stage === "online") return null;

  const isFailed = stage === "failed";
  const barPct   = isFailed ? 100 : (pct || 0);
  const label    = STAGE_LABELS[stage] || stage;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: isFailed ? "#f87171" : "#94a3b8", fontWeight: 600 }}>{label}</span>
        {!isFailed && <span style={{ fontSize: 11, color: "#4a6280", fontWeight: 700 }}>{barPct}%</span>}
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 99, width: `${barPct}%`,
          background: isFailed
            ? "linear-gradient(90deg,#ef4444,#b91c1c)"
            : "linear-gradient(90deg,#38bdf8,#6366f1)",
          transition: "width .6s ease",
          boxShadow: isFailed ? "0 0 8px #ef444466" : "0 0 8px #38bdf866",
        }} />
      </div>
      {isFailed && error && (
        <div style={{
          marginTop: 8, background: "rgba(127,29,29,0.35)",
          border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8,
          padding: "8px 10px", fontSize: 11, color: "#fca5a5",
          fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word", lineHeight: 1.5,
        }}>
          {classifyProvisionError(error) && (
            <div style={{ fontWeight: 700, marginBottom: 4, fontFamily: "inherit" }}>
              {classifyProvisionError(error).label}
            </div>
          )}
          {error}
        </div>
      )}
    </div>
  );
};

const GhostCard = () => (
  <div className="inst-card ghost-card">
    <div className="ghost-row" style={{ width: "60%", height: 14, marginBottom: 14 }} />
    <div className="ghost-row" style={{ width: "40%", height: 10, marginBottom: 8 }} />
    <div className="ghost-row" style={{ width: "70%", height: 13, marginBottom: 14 }} />
    <div className="ghost-row" style={{ width: "50%", height: 10, marginBottom: 8 }} />
    <div className="ghost-row" style={{ width: "85%", height: 13, marginBottom: 16 }} />
    <div className="ghost-row" style={{ width: "100%", height: 32, borderRadius: 8 }} />
  </div>
);

// Region flag for the chip buttons — matches the two live pop() regions.
const REGION_FLAG = { "New Delhi": "🇮🇳", "Frankfurt": "🇩🇪" };

export default function Iotamine() {
  const [instances, setInstances]   = useState([]);
  const [regions, setRegions]       = useState([]);
  const [osTemplates, setOsTemplates] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [ghostCount, setGhostCount] = useState(0);
  const [selected, setSelected]     = useState(new Set());
  const [toast, setToast]           = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmSelected, setConfirmSelected] = useState(false);
  const [confirmDelete, setConfirmDelete]     = useState(null);
  const [deletingIds, setDeletingIds]         = useState(new Set());
  const [retryingIds, setRetryingIds]         = useState(new Set());

  const [manualRegion, setManualRegion]     = useState("");
  const [manualCount, setManualCount]       = useState("1");
  const [manualCreating, setManualCreating] = useState(false);

  const token = () => localStorage.getItem("token") || "";
  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const loadInstances = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const r = await fetch(`${API}/iotamine/list`, { headers: { Authorization: "Bearer " + token() } });
      const d = await r.json();
      if (d.success) setInstances(d.instances || []);
    } catch {
      showToast("Failed to load instances", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (vpsId) => {
    setRetryingIds(p => new Set(p).add(vpsId));
    try {
      const r = await fetch(`${API}/iotamine/retry/${vpsId}`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (d.success) {
        showToast("Retry started");
        loadInstances(false);
      } else {
        showToast(d.detail || "Retry failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setRetryingIds(p => { const n = new Set(p); n.delete(vpsId); return n; });
    }
  };

  const loadMeta = async () => {
    try {
      const [rR, rO] = await Promise.all([
        fetch(`${API}/iotamine/regions`, { headers: { Authorization: "Bearer " + token() } }),
        fetch(`${API}/iotamine/os-templates`, { headers: { Authorization: "Bearer " + token() } }),
      ]);
      const regs = await rR.json();
      const oses = await rO.json();
      setRegions(Array.isArray(regs) ? regs : []);
      setOsTemplates(Array.isArray(oses) ? oses : []);
      if (Array.isArray(regs) && regs.length) setManualRegion(String(regs[0].id));
    } catch {
      showToast("Failed to load regions/OS — check IOTAMINE_API_KEY is set", "error");
    }
  };

  useEffect(() => { loadInstances(); loadMeta(); }, []);

  useEffect(() => {
    const hasActive = instances.some(i =>
      i.is_building ||
      (i.provision_stage && i.provision_stage !== "online" && i.provision_stage !== "failed")
    );
    if (!hasActive) return;
    const t = setTimeout(loadInstances, 6000);
    return () => clearTimeout(t);
  }, [instances]);

  // Ubuntu preferred; falls back to the first OS template if none match.
  const manualOsId = () => {
    const ubuntu = osTemplates.find(o => (o.name || "").toLowerCase().includes("ubuntu"));
    return ubuntu ? ubuntu.id : (osTemplates[0] ? osTemplates[0].id : null);
  };

  const handleCreateManual = async () => {
    const n = parseInt(manualCount, 10) || 0;
    if (!n || n < 1) { showToast("Enter a valid count", "error"); return; }
    if (!manualRegion) { showToast("Pick a region", "error"); return; }
    const osId = manualOsId();
    if (!osId) { showToast("No OS template available yet — try Refresh", "error"); return; }
    setManualCreating(true);
    setGhostCount(n);
    try {
      const r = await fetch(`${API}/iotamine/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({
          count: n, name_prefix: "Iotamine",
          pop: parseInt(manualRegion, 10), operating_system: osId,
        }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${d.created.length} VPS created [${SPEC_CORES}c/${SPEC_RAM}GB]!`);
        await loadInstances();
      } else {
        showToast(`${d.created?.length || 0} created, ${d.failed?.length || 0} failed`, "warn");
        await loadInstances();
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setManualCreating(false);
      setGhostCount(0);
    }
  };

  const handleDeleteOne = async (id) => {
    setConfirmDelete(null);
    setDeletingIds(prev => new Set([...prev, id]));
    try {
      const r = await fetch(`${API}/iotamine/delete/${id}`, { method: "DELETE", headers: { Authorization: "Bearer " + token() } });
      const d = await r.json();
      if (d.success) {
        showToast("Instance terminated");
        setInstances(prev => prev.filter(i => i.instance_id !== id));
        setSelected(prev => { const s = new Set(prev); s.delete(id); return s; });
      } else {
        showToast(d.detail || "Terminate failed", "error");
      }
    } catch {
      showToast("Terminate failed", "error");
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) { showToast("Select instances first", "error"); return; }
    setConfirmSelected(false);
    const ids = [...selected];
    setDeletingIds(new Set(ids));
    try {
      const r = await fetch(`${API}/iotamine/delete-selected`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ instance_ids: ids }),
      });
      const d = await r.json();
      if (d.success) { showToast(`${ids.length} instance(s) terminated`); setSelected(new Set()); }
      else showToast("Some deletes failed", "error");
      await loadInstances(false);
    } catch {
      showToast("Terminate failed", "error");
      await loadInstances(false);
    } finally {
      setDeletingIds(new Set());
    }
  };

  const handleDeleteAll = async () => {
    setConfirmAll(false);
    const allIds = instances.map(i => i.instance_id);
    setDeletingIds(new Set(allIds));
    try {
      const r = await fetch(`${API}/iotamine/delete-all`, { method: "DELETE", headers: { Authorization: "Bearer " + token() } });
      const d = await r.json();
      if (d.success) { showToast(`All ${allIds.length} instance(s) terminated`); setInstances([]); setSelected(new Set()); }
      else { showToast("Terminate all failed", "error"); await loadInstances(false); }
    } catch {
      showToast("Terminate all failed", "error");
      await loadInstances(false);
    } finally {
      setDeletingIds(new Set());
    }
  };

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => { if (selected.size === instances.length) setSelected(new Set()); else setSelected(new Set(instances.map(i => i.instance_id))); };

  const runningCount  = instances.filter(i => (i.status || "").toLowerCase() === "running").length;
  const buildingCount = instances.filter(i => i.is_building).length;
  const failedCount   = instances.filter(i => i.is_failed || i.provision_stage === "failed").length;
  const provisioningCount = instances.filter(i =>
    i.provision_stage && i.provision_stage !== "online" && i.provision_stage !== "failed"
  ).length;

  return (
    <div style={{ animation: "fadeIn .2s ease" }}>
      <style>{`
        .tencent-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
        .inst-card { background: rgba(8,13,32,0.72); backdrop-filter: blur(28px) saturate(1.5); border: 1px solid rgba(255,255,255,0.09); border-radius: 16px; padding: 18px; box-shadow: 0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06); transition: border-color .25s, box-shadow .25s, transform .2s; }
        .inst-card:hover { border-color: rgba(99,179,237,0.2); box-shadow: 0 14px 58px rgba(0,0,0,.65), 0 0 0 1px rgba(99,179,237,0.08), inset 0 1px 0 rgba(255,255,255,.06); }
        .inst-card.selected { border-color: rgba(56,189,248,0.45); background: rgba(56,189,248,0.05); }
        .inst-meta-label { font-size: 10px; color: #334155; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px; font-weight: 700; }
        .inst-ip { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #38bdf8; font-weight: 600; }
        .inst-id { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #4a6280; }
        .ghost-card { display: flex; flex-direction: column; pointer-events: none; }
        .ghost-row { background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 37%, rgba(255,255,255,0.05) 63%); background-size: 400% 100%; animation: ghostShimmer 1.4s ease infinite; border-radius: 6px; }
        @keyframes ghostShimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
        .mini-stat { background: rgba(8,13,32,0.72); backdrop-filter: blur(28px) saturate(1.5); border: 1px solid rgba(255,255,255,0.09); border-radius: 14px; padding: 14px 18px; flex: 1; min-width: 110px; }
        .mini-stat-val { font-size: 26px; font-weight: 900; letter-spacing: -1px; line-height: 1; margin-bottom: 4px; }
        .mini-stat-lbl { font-size: 10px; letter-spacing: 1.4px; text-transform: uppercase; font-weight: 800; color: #4a6280; }
        .count-field-wrap { position: relative; display: flex; align-items: center; }
        .count-field { width: 110px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.13); color: #e2e8f0; border-radius: 9px; padding: 9px 13px; font-size: 15px; font-weight: 700; font-family: 'Outfit', sans-serif; outline: none; transition: border-color .18s, box-shadow .18s; }
        .count-field:focus { border-color: rgba(99,179,237,0.55); box-shadow: 0 0 0 3px rgba(99,179,237,0.09); }
        .count-step { width: 32px; height: 32px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05); color: #94a3b8; cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center; transition: all .15s; flex-shrink: 0; }
        .count-step:hover { background: rgba(255,255,255,0.1); color: #e2e8f0; }
        @media(max-width:600px) {
          .tencent-create-row { flex-wrap: wrap; gap: 12px !important; }
          .tencent-grid { grid-template-columns: 1fr; }
          .mini-stat-row { flex-wrap: wrap; }
        }
      `}</style>

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 16, zIndex: 9999, background: toast.type === "error" ? "rgba(127,29,29,0.97)" : toast.type === "warn" ? "rgba(120,53,15,0.97)" : "rgba(20,83,45,0.97)", border: `1px solid ${toast.type === "error" ? "#ef4444" : toast.type === "warn" ? "#f59e0b" : "#22c55e"}`, color: "#fff", borderRadius: 12, padding: "12px 20px", boxShadow: "0 4px 24px #0009", fontSize: 14, fontWeight: 600, backdropFilter: "blur(16px)", maxWidth: "calc(100vw - 32px)" }}>{toast.msg}</div>
      )}

      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>Iotamine</h2>
          <p style={{ color: "#4a6280", fontSize: 13, margin: "4px 0 0" }}>New Delhi / Frankfurt · {SPEC_CORES}c / {SPEC_RAM}GB / {SPEC_DISK}GB · Ubuntu</p>
        </div>
        <button className="adm-btn adm-btn-sm" onClick={() => loadInstances()} disabled={loading}>{loading ? "..." : "Refresh"}</button>
      </div>

      <div className="mini-stat-row" style={{ display: "flex", gap: 14, marginBottom: 20 }}>
        <div className="mini-stat"><div className="mini-stat-val" style={{ color: "#f1f5f9" }}>{instances.length}</div><div className="mini-stat-lbl">Total VPS</div></div>
        <div className="mini-stat"><div className="mini-stat-val" style={{ color: "#4ade80" }}>{runningCount}</div><div className="mini-stat-lbl">Running</div></div>
        {buildingCount > 0 && <div className="mini-stat"><div className="mini-stat-val" style={{ color: "#fbbf24" }}>{buildingCount}</div><div className="mini-stat-lbl">Building</div></div>}
        {provisioningCount > 0 && <div className="mini-stat"><div className="mini-stat-val" style={{ color: "#818cf8" }}>{provisioningCount}</div><div className="mini-stat-lbl">Installing Agent</div></div>}
        {failedCount > 0 && <div className="mini-stat"><div className="mini-stat-val" style={{ color: "#f87171" }}>{failedCount}</div><div className="mini-stat-lbl">Failed</div></div>}
      </div>

      {/* Manual Instance Create — one click, same pattern as Tencent's manual-instance section */}
      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Manual Instances</div>
        <div style={{ fontSize: 12, color: "#4a6280", marginBottom: 14 }}>{SPEC_CORES}c / {SPEC_RAM}GB RAM / {SPEC_DISK}GB disk · bandwidth default · Ubuntu — pick region, click create</div>
        <div className="tencent-create-row" style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div>
            <label className="adm-label">Region</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {regions.length === 0 && <span style={{ color: "#4a6280", fontSize: 13, padding: "10px 0" }}>Loading...</span>}
              {regions.map(r => (
                <button
                  key={r.id}
                  onClick={() => setManualRegion(String(r.id))}
                  disabled={manualCreating}
                  style={{
                    padding: "10px 16px", borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${manualRegion === String(r.id) ? "#38bdf8" : "rgba(255,255,255,0.1)"}`,
                    background: manualRegion === String(r.id) ? "#38bdf818" : "rgba(255,255,255,0.04)",
                    color: manualRegion === String(r.id) ? "#38bdf8" : "#64748b",
                    fontWeight: 700, fontSize: 13, fontFamily: "'Outfit', sans-serif",
                    transition: "all .15s",
                  }}
                >
                  <div>{REGION_FLAG[r.city] || ""} {r.city}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, opacity: 0.8 }}>{SPEC_CORES}c / {SPEC_RAM}GB</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="adm-label">Count</label>
            <div className="count-field-wrap" style={{ gap: 8 }}>
              <button className="count-step" onClick={() => setManualCount(String(Math.max(1, parseInt(manualCount || 1, 10) - 1)))} disabled={manualCreating}>−</button>
              <input
                className="count-field"
                type="text" inputMode="numeric" placeholder="1"
                value={manualCount} disabled={manualCreating}
                onChange={e => setManualCount(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !manualCreating) handleCreateManual(); }}
              />
              <button className="count-step" onClick={() => setManualCount(String(parseInt(manualCount || 0, 10) + 1))} disabled={manualCreating}>+</button>
            </div>
          </div>
          <button
            className="adm-btn adm-btn-primary"
            onClick={handleCreateManual}
            disabled={manualCreating}
            style={{ marginLeft: "auto" }}
          >
            {manualCreating ? "Creating..." : "Create Manual Instance"}
          </button>
        </div>
      </div>

      {instances.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "#94a3b8" }}>
            <input type="checkbox" checked={selected.size === instances.length} onChange={toggleAll} />
            Select All ({selected.size}/{instances.length})
          </label>
          {selected.size > 0 && <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmSelected(true)}>Terminate Selected ({selected.size})</button>}
          <button className="adm-btn adm-btn-danger adm-btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmAll(true)}>Terminate All</button>
        </div>
      )}

      {loading && instances.length === 0 && ghostCount === 0 ? (
        <div className="adm-card adm-card-pad adm-empty"><div style={{ fontSize: 28, marginBottom: 8 }}>...</div>Loading VPS...</div>
      ) : instances.length === 0 && ghostCount === 0 ? (
        <div className="adm-card adm-card-pad adm-empty"><div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>No VPS yet. Create one above.</div>
      ) : (
        <div className="tencent-grid">
          {Array.from({ length: ghostCount }).map((_, i) => <GhostCard key={`ghost-${i}`} />)}
          {instances.map(inst => (
            <div key={inst.instance_id} className={`inst-card${selected.has(inst.instance_id) ? " selected" : ""}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={selected.has(inst.instance_id)} onChange={() => toggleSelect(inst.instance_id)} style={{ width: 15, height: 15, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{inst.name}</span>
                </label>
                <StatusBadge status={inst.status} isBuilding={inst.is_building} isFailed={inst.is_failed} />
              </div>
              <div style={{ marginBottom: 12 }}><span className="adm-badge adm-badge-gray">{inst.region || "Unknown"}</span></div>
              <ProvisionBar stage={inst.provision_stage} pct={inst.provision_pct} error={inst.provision_error} />
              <div style={{ marginBottom: 10 }}>
                <div className="inst-meta-label">Public IP</div>
                {inst.public_ip ? <span className="inst-ip">{inst.public_ip}</span> : <span style={{ color: "#4a6280", fontSize: 13 }}>Pending...</span>}
              </div>
              <div style={{ marginBottom: 10 }}>
                <div className="inst-meta-label">VPS ID</div>
                <span className="inst-id">{inst.instance_id}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <SpecBadge cores={inst.cores} ram={inst.ram} disk={inst.disk} />
                {inst.hourly_rate != null && <span className="adm-badge adm-badge-gray">₹{inst.hourly_rate}/hr</span>}
              </div>
              {inst.provision_error && (
                <button
                  className="adm-btn adm-btn-sm"
                  style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}
                  onClick={() => handleRetry(inst.instance_id)}
                  disabled={retryingIds.has(inst.instance_id)}
                >
                  {retryingIds.has(inst.instance_id) ? "Retrying…" : "↻ Retry"}
                </button>
              )}
              <button
                className="adm-btn adm-btn-danger adm-btn-sm"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => setConfirmDelete({ id: inst.instance_id, name: inst.name })}
                disabled={deletingIds.has(inst.instance_id)}
              >
                {deletingIds.has(inst.instance_id) ? "Terminating..." : "Terminate Instance"}
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Terminate Instance?"
        message={<>Are you sure you want to terminate <strong style={{ color: "#f87171" }}>{confirmDelete?.name || confirmDelete?.id}</strong>? This cannot be undone.</>}
        confirmLabel="Terminate"
        onConfirm={() => handleDeleteOne(confirmDelete?.id)}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmModal
        open={confirmSelected}
        title={`Terminate ${selected.size} Instance(s)?`}
        message={<>This will permanently terminate <strong style={{ color: "#f87171" }}>{selected.size} selected instance(s)</strong>. This cannot be undone.</>}
        confirmLabel="Terminate Selected"
        onConfirm={handleDeleteSelected}
        onCancel={() => setConfirmSelected(false)}
      />
      <ConfirmModal
        open={confirmAll}
        title="Terminate All Instances?"
        message={<>This will permanently terminate <strong style={{ color: "#f87171" }}>ALL {instances.length} instance(s)</strong>. This cannot be undone.</>}
        confirmLabel="Terminate All"
        onConfirm={handleDeleteAll}
        onCancel={() => setConfirmAll(false)}
      />
    </div>
  );
}
